import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import Logo from './Logo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const navItems = [
    { path: '/', label: { sl: 'Domov', en: 'Home', it: 'Home' } },
    { path: '/services', label: { sl: 'Storitve', en: 'Services', it: 'Servizi' } },
    { path: '/portfolio', label: { sl: 'Naši projekti', en: 'Our Projects', it: 'I Nostri Progetti' } },
    { path: '/about', label: { sl: 'O nas', en: 'About Us', it: 'Chi Siamo' } },
    { path: '/contact', label: { sl: 'Kontakt', en: 'Contact', it: 'Contatti' } }
  ];

  const languages = [
    { code: 'sl', label: 'SLO' },
    { code: 'en', label: 'ENG' },
    { code: 'it', label: 'ITA' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10 backdrop-blur-sm">
      <div className="container mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo width="160" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link text-sm font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? 'nav-link-active'
                    : 'text-gray-400 hover:text-white'
                }`}
                style={{ fontFamily: "'Exo 2', sans-serif" }}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                {t(item.label)}
              </Link>
            ))}
            
            {/* Language Dropdown */}
            <div className="relative">
              <Button
                onClick={() => setShowLangMenu(!showLangMenu)}
                variant="outline"
                size="sm"
                className="bg-transparent border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 transition-all duration-300 rounded-lg px-4 py-2"
              >
                <Globe className="w-4 h-4 mr-2" />
                {language.toUpperCase()}
              </Button>
              
              {showLangMenu && (
                <div className="absolute top-full right-0 mt-2 bg-black border border-white/20 rounded-lg overflow-hidden shadow-xl">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLangMenu(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                        language === lang.code
                          ? 'bg-white/10 text-white'
                          : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-5 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex flex-col divide-y divide-white/10">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`nav-link flex items-center min-h-[52px] text-lg font-semibold ${
                    location.pathname === item.path
                      ? 'text-white'
                      : 'text-gray-400'
                  }`}
                  style={{ fontFamily: "'Exo 2', sans-serif" }}
                  data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                >
                  {t(item.label)}
                </Link>
              ))}
              
              {/* Mobile Language Selector */}
              <div className="flex gap-2 pt-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsOpen(false);
                    }}
                    className={`flex-1 min-h-[48px] px-4 text-sm font-semibold rounded-xl border-2 transition-all ${
                      language === lang.code
                        ? 'bg-white/10 text-white border-white/40'
                        : 'text-gray-400 border-white/20 hover:bg-white/5'
                    }`}
                    data-testid={`mobile-lang-${lang.code}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
